import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  fetchPatientMealInstances,
  createPatientMealInstance,
  fetchPatientMealInstanceById,
  updatePatientMealInstance,
  deletePatientMealInstance,
  addExtraComponent,
  updateExtraComponent,
  deleteExtraComponent
} from '../../api/api_patient_meal_instances';

export const fetchPatientMealInstancesThunk = createAsyncThunk(
  'patientMealInstances/fetchPatientMealInstances',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await fetchPatientMealInstances(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const createPatientMealInstanceThunk = createAsyncThunk(
  'patientMealInstances/createPatientMealInstance',
  async (instanceData, { rejectWithValue }) => {
    try {
      const response = await createPatientMealInstance(instanceData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const fetchPatientMealInstanceByIdThunk = createAsyncThunk(
  'patientMealInstances/fetchPatientMealInstanceById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await fetchPatientMealInstanceById(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const updatePatientMealInstanceThunk = createAsyncThunk(
  'patientMealInstances/updatePatientMealInstance',
  async ({ id, instanceData }, { rejectWithValue }) => {
    try {
      const response = await updatePatientMealInstance(id, instanceData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const deletePatientMealInstanceThunk = createAsyncThunk(
  'patientMealInstances/deletePatientMealInstance',
  async (id, { rejectWithValue }) => {
    try {
      await deletePatientMealInstance(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const addExtraComponentThunk = createAsyncThunk(
  'patientMealInstances/addExtraComponent',
  async ({ instanceId, componentData }, { rejectWithValue }) => {
    try {
      const response = await addExtraComponent(instanceId, componentData);
      return { instanceId, component: response.data.data || response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const updateExtraComponentThunk = createAsyncThunk(
  'patientMealInstances/updateExtraComponent',
  async ({ instanceId, componentId, componentData }, { rejectWithValue }) => {
    try {
      const response = await updateExtraComponent(instanceId, componentId, componentData);
      return { instanceId, component: response.data.data || response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const deleteExtraComponentThunk = createAsyncThunk(
  'patientMealInstances/deleteExtraComponent',
  async ({ instanceId, componentId }, { rejectWithValue }) => {
    try {
      await deleteExtraComponent(instanceId, componentId);
      return { instanceId, componentId };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

const patientMealInstancesSlice = createSlice({
  name: 'patientMealInstances',
  initialState: {
    instances: [],
    currentInstance: null,
    pagination: null,
    loading: false,
    error: null,
    status: 'idle',
  },
  reducers: {
    clearCurrentInstance: (state) => {
      state.currentInstance = null;
    },
    setCurrentInstance: (state, action) => {
      state.currentInstance = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch patient meal instances
      .addCase(fetchPatientMealInstancesThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPatientMealInstancesThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.instances = action.payload.data || [];
        state.pagination = action.payload.pagination || null;
        state.status = 'succeeded';
      })
      .addCase(fetchPatientMealInstancesThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.status = 'failed';
      })
      // Create patient meal instance
      .addCase(createPatientMealInstanceThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPatientMealInstanceThunk.fulfilled, (state, action) => {
        state.loading = false;
        const newInstance = action.payload.data || action.payload;
        state.instances.unshift(newInstance);
        state.status = 'succeeded';
      })
      .addCase(createPatientMealInstanceThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.status = 'failed';
      })
      // Fetch patient meal instance by ID
      .addCase(fetchPatientMealInstanceByIdThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPatientMealInstanceByIdThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.currentInstance = action.payload.data || action.payload;
        state.status = 'succeeded';
      })
      .addCase(fetchPatientMealInstanceByIdThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.status = 'failed';
      })
      // Update patient meal instance
      .addCase(updatePatientMealInstanceThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePatientMealInstanceThunk.fulfilled, (state, action) => {
        state.loading = false;
        const updatedInstance = action.payload.data || action.payload;
        const index = state.instances.findIndex(i => i.id === updatedInstance.id);
        if (index !== -1) {
          state.instances[index] = updatedInstance;
        }
        if (state.currentInstance && state.currentInstance.id === updatedInstance.id) {
          state.currentInstance = updatedInstance;
        }
        state.status = 'succeeded';
      })
      .addCase(updatePatientMealInstanceThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.status = 'failed';
      })
      // Delete patient meal instance
      .addCase(deletePatientMealInstanceThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deletePatientMealInstanceThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.instances = state.instances.filter(i => i.id !== action.payload);
        if (state.currentInstance && state.currentInstance.id === action.payload) {
          state.currentInstance = null;
        }
        state.status = 'succeeded';
      })
      .addCase(deletePatientMealInstanceThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.status = 'failed';
      })
      // Add extra component
      .addCase(addExtraComponentThunk.fulfilled, (state, action) => {
        if (state.currentInstance && state.currentInstance.id === action.payload.instanceId) {
          if (!state.currentInstance.extraComponents) {
            state.currentInstance.extraComponents = [];
          }
          state.currentInstance.extraComponents.push(action.payload.component);
        }
      })
      // Update extra component
      .addCase(updateExtraComponentThunk.fulfilled, (state, action) => {
        if (state.currentInstance && state.currentInstance.id === action.payload.instanceId) {
          const index = state.currentInstance.extraComponents.findIndex(
            c => c.id === action.payload.component.id
          );
          if (index !== -1) {
            state.currentInstance.extraComponents[index] = action.payload.component;
          }
        }
      })
      // Delete extra component
      .addCase(deleteExtraComponentThunk.fulfilled, (state, action) => {
        if (state.currentInstance && state.currentInstance.id === action.payload.instanceId) {
          state.currentInstance.extraComponents = state.currentInstance.extraComponents.filter(
            c => c.id !== action.payload.componentId
          );
        }
      });
  },
});

export const { clearCurrentInstance, setCurrentInstance } = patientMealInstancesSlice.actions;
export default patientMealInstancesSlice.reducer;

